'use client'

import { memo, useEffect } from 'react'
import { Provider } from 'react-redux'

import { DevMarker } from '@components/Layout/DevMarker'
import { PageTemplate } from '@components/Layout/PageTemplate'
import SocketRouteBridgePages from '@components/SocketRouteBridgePages'
import { WsDebugOverlay } from '@features/websocket/debug/WsDebugOverlay'
import { Spin } from '@fp/ui-kit'
import { useAnalytics } from '@hooks/useAnalytics'
import { useConnectionStatus } from '@hooks/useConnectionStatus'
import { useGetCurrentSessionMirrorToRedux } from '@hooks/useGetCurrentSessionMirrorToRedux'
import { useRole } from '@hooks/useRole'
import { apiRedirectUrl } from '@lib/constants'
import { store } from '@store/store'

const SessionLayout = memo(({ children }: { children: React.ReactNode }) => {
  const { checkIsAuthorized } = useRole()
  useAnalytics()
  useConnectionStatus()
  const { session, isSuccess, isFetched, isPending, isRefetchError, isError } = useGetCurrentSessionMirrorToRedux()

  const isSuccessLogin = isSuccess && session?.email
  const isAuthorized = checkIsAuthorized(session?.role ?? '')

  useEffect(() => {
    if (!isFetched || isPending) {
      return
    }

    if (isRefetchError || isError) {
      window.location.href = '/login'
    } else if (!isAuthorized) {
      window.location.href = apiRedirectUrl
    }
  }, [isError, isFetched, isAuthorized, isPending, isRefetchError])

  const isLoading = !isFetched || isPending
  if (isLoading) {
    return (
      <div className='flex h-screen w-screen items-center justify-center bg-background'>
        <Spin size='xl' className='text-muted-foreground animate-fade-in' />
      </div>
    )
  }

  const isNotAllowed = isError || isRefetchError || !isAuthorized || !isSuccessLogin
  if (isNotAllowed) {
    return null
  }

  return (
    <PageTemplate>
      {children}

      <SocketRouteBridgePages />
      <WsDebugOverlay />
      <DevMarker />
    </PageTemplate>
  )
})

export default function RootLayout({ children }: { children: React.ReactNode }) {
  return (
    <Provider store={store}>
      <SessionLayout>{children}</SessionLayout>
    </Provider>
  )
}
